const stringifiableTypes: readonly string[] = [
    "boolean",
    "number",
    "string", // stringify Strings so that they are quoted in the output
] as const;

/**
 * Canonicalizes a value to a string representation suitable for hashing.
 *
 * @param val - The value to canonicalize.
 * @return The canonical string representation of the value.
 * @remarks We use a custom canonicalization to ensure that the string
 * representation is consistent and predictable for hashing.
 */
function canonicalize(val: any): string {
    // We could just use JSON.stringify on everything, but then we'd lose
    // control over the order of object keys, which could produce unexpected
    // results.

    const valType: string = typeof val;
    if (val === null || stringifiableTypes.includes(valType)) {
        return JSON.stringify(val);
    }

    if (Array.isArray(val)) {
        const items = val.map(canonicalize).join(",");

        return `[${items}]`;
    }

    if (valType === "object") {
        const keys = Object.keys(val).sort();
        const entries = keys.map((key) => {
            const keyStr = JSON.stringify(key);
            const value = canonicalize(val[key]);

            return `${keyStr}:${value}`;
        }).join(",");

        return `{${entries}}`;
    }

    return "null";
}

/**
 * Computes the FNV-1a hash for a given string.
 *
 * @param str - The input string to hash.
 * @return The computed hash as a 32-bit unsigned integer.
 */
function fnv1a32(str: string): number {
    let hash = 0x811c9dc5; // FNV offset basis

    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);

        // multiply by FNV prime (mod 2^32)
        hash = (hash >>> 0) * 0x01000193 >>> 0;
    }

    return hash >>> 0; // ensure unsigned
}

/**
 * Calculates a checksum for the input.
 *
 * @param value - The value to calculate the checksum for.
 * @return The checksum as a 32-bit unsigned integer.
 */
export function checksum(value: any): number {
    const canon = canonicalize(value);

    return fnv1a32(canon);
}
